import { useIsReadOnly } from '@/lib/casl'
import { useFieldContext } from '@/lib/form-contexts'
import { cn } from '@/lib/utils'
import { Percent } from 'lucide-react'
import type { ReactNode } from 'react'
import { InputGroup, type InputGroupProps } from '../ui/input-group'
import { Label } from '../ui/label'
import { Typography } from '@/components/ui/typography'
import FieldInfo from './FieldInfo'

export type PercentInputProps = Omit<InputGroupProps, 'max' | 'min'> & {
  label?: ReactNode
  labelEnd?: ReactNode
  inputWrapperClassName?: string
  readOnlyEmptyText?: ReactNode
  clampOnBlur?: boolean
  decimalPlaces?: number
  max?: number
  min?: number
}

export default function PercentInput({
  id,
  label,
  type,
  labelEnd,
  placeholder = '0',
  className,
  inputWrapperClassName,
  readOnlyEmptyText = '',
  clampOnBlur = true,
  decimalPlaces = 2,
  max = 100,
  min = 0,
  ...props
}: PercentInputProps) {
  const field = useFieldContext<string | number | null | undefined>()
  const { isReadOnly } = useIsReadOnly()

  const sanitizeValue = (v: string) => {
    if (v === '' || v == null) return ''

    const normalized = String(v).replace(/[^0-9.]/g, '')
    if (!normalized) return ''

    const parts = normalized.split('.')
    const integerPart = (parts[0] || '').replace(/^0+(?=\d)/, '')
    const decimalPart = parts.slice(1).join('').slice(0, decimalPlaces)
    const hasDecimal = normalized.includes('.')

    if (hasDecimal) {
      return `${integerPart || '0'}.${decimalPart}`
    }

    return integerPart || '0'
  }

  const clampValue = (v: string) => {
    if (v === '' || v == null) return ''
    const normalized = sanitizeValue(v)
    const num = Number.parseFloat(normalized)
    if (!Number.isFinite(num)) return ''
    const clamped = Math.min(max, Math.max(min, num))
    const factor = 10 ** decimalPlaces
    return String(Math.round(clamped * factor) / factor)
  }

  return (
    <div className="flex flex-col gap-y-2 w-full">
      {!!label && (
        <div className="inline-flex items-center">
          <Label htmlFor={id || field.name}>{label}</Label>
          {labelEnd}
        </div>
      )}

      {isReadOnly ? (
        <Typography variant="supporting" tone={!field.state.value ? 'muted' : 'default'}>
          {field.state.value ?? readOnlyEmptyText}
        </Typography>
      ) : (
        <InputGroup
          id={id || field.name}
          type="text"
          inputMode="decimal"
          placeholder={placeholder}
          value={field.state.value ?? ''}
          onChange={(e) => {
            const next = sanitizeValue(e.target.value)
            field.handleChange(next)
          }}
          onBlur={(e) => {
            field.handleChange(
              clampOnBlur ? clampValue(e.target.value) : sanitizeValue(e.target.value)
            )
            field.handleBlur()
          }}
          startAdornment={<Percent aria-hidden="true" />}
          className={cn(className, inputWrapperClassName)}
          inputClassName={className}
          {...props}
        />
      )}

      {type !== 'hidden' && <FieldInfo />}
    </div>
  )
}
